import { useEffect, useState } from "react";
import { Mail, Search, Filter, Archive, Trash2, Reply, Forward, Star } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { SharetribeRepository } from "@/infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";

export function InboxContent() {
  const repo = new SharetribeRepository(apiService);

  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadInbox = async () => {
    try {
      setLoading(true);

      const userIsCoach = !!localStorage.getItem("coach_id");

      const include = userIsCoach
        ? ["customer.profileImage", "booking", "listing"]
        : ["provider.profileImage", "booking", "listing"];

      const only = userIsCoach ? "sale" : "order";

      const res = await repo.transactionsQuery(only, null, include);

      if (!res) {
        setMessages([]);
        return;
      }

      const transactionData: any[] = [];

      for (const trans of res) {
        const included = trans.included || [];

        const listingId = trans.relationships?.listing?.data?.id;
        const listingObj = included.find((x: any) => x.id === listingId);
        const listingDeleted = listingObj?.attributes?.deleted;

        if (!userIsCoach || listingDeleted === false) {
          transactionData.push(trans);
        }
      }

      const sorted = transactionData.sort((a: any, b: any) => {
        const d1 = new Date(a.attributes.lastTransitionedAt).getTime();
        const d2 = new Date(b.attributes.lastTransitionedAt).getTime();
        return d2 - d1;
      });

      const mapped = sorted.map((t: any) => {
        const attr = t.attributes;
        const included = t.included;

        const customerId = t.relationships?.customer?.data?.id;
        const customerObj = included.find((x: any) => x.id === customerId);
        const displayName = customerObj?.attributes?.profile?.displayName || "Unknown";

        const imgId = customerObj?.relationships?.profileImage?.data?.id;
        const imgObj = included.find((x: any) => x.id === imgId);
        const profileUrl = imgObj?.attributes?.variants?.default?.url;

        return {
          id: t.id,
          from: displayName,
          subject: "New update",
          preview: "Tap to view transaction details",
          time: formatDate(attr.lastTransitionedAt),
          profilePic: profileUrl,
          lastTransition: attr.lastTransition,
          isRead: isTransitionRead(attr.lastTransition),
          priority: attr.lastTransition === "transition/confirm-payment" ? "high" : "normal",
          raw: t
        };
      });

      setMessages(mapped);
    } catch (err) {
      console.error("Inbox load error", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInbox();
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
       </div>
    );
  }

  return (
    <div className="space-y-6">

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inbox</h1>
          <p className="text-muted-foreground">Your messages and notifications</p>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="icon">
            <Filter className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon">
            <Archive className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search messages..." className="pl-10" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {messages.filter((m) => !m.isRead).length}
            </div>
            <div className="text-sm text-muted-foreground">Unread</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 text-center">
            <div className="text-2xl font-bold text-green-600">{messages.length}</div>
            <div className="text-sm text-muted-foreground">Total Messages</div>
          </CardContent>
        </Card>

      </div>

      <div className="space-y-2">
        {messages.map((msg) => (
          <Card
            key={msg.id}
            className={`hover:shadow-md transition cursor-pointer ${
              !msg.isRead ? "border-l-4 border-blue-500" : ""
            }`}
          >
            <CardContent className="p-4 flex space-x-4">

              <Avatar className="h-10 w-10">
                <AvatarImage src={msg.profilePic || "/placeholder-avatar.jpg"} />
                <AvatarFallback>{msg.from?.[0] || "U"}</AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between">
                  <div className="flex space-x-2 items-center">
                    <h3 className={`font-semibold ${!msg.isRead ? "font-bold" : ""}`}>
                      {msg.from}
                    </h3>

                    {msg.priority === "high" && (
                      <Badge variant="destructive" className="text-xs">High</Badge>
                    )}

                    {msg.lastTransition === "transition/confirm-payment" && (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    )}
                  </div>

                  <span className="text-sm text-muted-foreground">{msg.time}</span>
                </div>

                <p className="text-sm text-muted-foreground truncate">
                  {msg.preview}
                </p>
              </div>

              <div className="flex items-center space-x-1">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Reply className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Forward className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

            </CardContent>
          </Card>
        ))}
      </div>

      {messages.length === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="font-semibold mb-2">No messages</h3>
            <p className="text-muted-foreground">No inbox items yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function formatDate(date: string) {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}

function isTransitionRead(transition: string) {
  return (
    transition !== "transition/confirm-payment" &&
    transition !== "transition/enquire"
  );
}